import React from 'react';
import {
    Card,
    Collapse,
    Descriptions,
    Space,
    Table,
    Tabs,
    Tag,
    Typography,
} from 'antd';
import {
    ApiOutlined,
    CodeOutlined,
    QrcodeOutlined,
    QuestionCircleOutlined,
} from '@ant-design/icons';

const { Title, Paragraph, Text, Link } = Typography;

const BASE = process.env.PUBLIC_URL;

const FLOW_TABS = [
    {
        key: 'app1',
        label: 'Ticket flow — first login',
        children: (
            <img
                src={`${BASE}/diagrams/sso_ticket_app1.svg`}
                alt="ChulaSSO ticket flow — first login"
                style={{ maxWidth: '100%', height: 'auto' }}
            />
        ),
    },
    {
        key: 'app2',
        label: 'Ticket flow — already authenticated',
        children: (
            <img
                src={`${BASE}/diagrams/sso_ticket_app2.svg`}
                alt="ChulaSSO ticket flow — already authenticated"
                style={{ maxWidth: '100%', height: 'auto' }}
            />
        ),
    },
    {
        key: 'qr',
        label: <><QrcodeOutlined /> QR login</>,
        children: (
            <img
                src={`${BASE}/diagrams/sso_qr_login.svg`}
                alt="ChulaSSO QR login flow"
                style={{ maxWidth: '100%', height: 'auto' }}
            />
        ),
    },
];

const PROFILE_FIELDS = [
    { field: 'uid',          type: 'string',   desc: 'Student / staff ID number' },
    { field: 'username',     type: 'string',   desc: 'Chula IT account name (e.g. pkrerk)' },
    { field: 'firstname',    type: 'string',   desc: 'First name (English)' },
    { field: 'lastname',     type: 'string',   desc: 'Last name (English)' },
    { field: 'firstnameTH',  type: 'string',   desc: 'First name (Thai)' },
    { field: 'lastnameTH',   type: 'string',   desc: 'Last name (Thai)' },
    { field: 'email',        type: 'string',   desc: 'University email address' },
    { field: 'gecos',        type: 'string',   desc: 'LDAP display string (name, faculty, employee no.)' },
    { field: 'roles',        type: 'string[]', desc: '"student", "faculty", or both' },
    { field: 'ouid',         type: 'string',   desc: 'Organizational unit / employee number (may be null)' },
    { field: 'disable',      type: 'boolean',  desc: 'True if the account has been disabled' },
];

const PROFILE_COLUMNS = [
    {
        title: 'Field',
        dataIndex: 'field',
        key: 'field',
        width: 140,
        render: (v) => <Text code>{v}</Text>,
    },
    {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 90,
        render: (v) => <Text type="secondary">{v}</Text>,
    },
    { title: 'Description', dataIndex: 'desc', key: 'desc' },
];

const FAQ = [
    {
        key: '1',
        label: 'I forgot my password or cannot log in',
        children: (
            <Paragraph style={{ marginBottom: 0 }}>
                Contact the <Link href="https://www.it.chula.ac.th/" target="_blank" rel="noreferrer">
                Office of Information Technology (OIT)
                </Link>, not ChulaSSO. ChulaSSO delegates authentication to the university LDAP —
                account management belongs to OIT.
            </Paragraph>
        ),
    },
    {
        key: '2',
        label: 'Does it work for both students and staff?',
        children: (
            <Paragraph style={{ marginBottom: 0 }}>
                Yes. Any account in the Chula LDAP — students, faculty, and staff — can authenticate.
            </Paragraph>
        ),
    },
    {
        key: '3',
        label: 'How do I register my application?',
        children: (
            <Paragraph style={{ marginBottom: 0 }}>
                Log in and go to <Link href="/html/myapps">Developer — My Apps</Link>. Submit a
                self-service request with your app name, description, and callback URL. After admin
                approval you receive an <Text code>app_id</Text> and <Text code>app_secret</Text>.
                For questions, contact <Link href="mailto:Krerk.P@chula.ac.th">Krerk.P@chula.ac.th</Link>.
            </Paragraph>
        ),
    },
    {
        key: '4',
        label: 'Can I use the ChulaSSO badge on my login page?',
        children: (
            <Paragraph style={{ marginBottom: 0 }}>
                Yes. Use the official badge image at{' '}
                <Text code>https://account.it.chula.ac.th/images/ConnectWithChulaSSO.png</Text>{' '}
                unmodified on your login page.
            </Paragraph>
        ),
    },
    {
        key: '5',
        label: 'When should I use QR login vs. the standard ticket flow?',
        children: (
            <Paragraph style={{ marginBottom: 0 }}>
                Use the <strong>ticket flow</strong> for web apps where the user has a browser.
                Use <strong>QR login</strong> for kiosks, hardware terminals, or any device that cannot
                perform a browser redirect — the scanner just needs to POST the scanned token to
                your backend.
            </Paragraph>
        ),
    },
    {
        key: '6',
        label: 'Are there sample apps?',
        children: (
            <Paragraph style={{ marginBottom: 0 }}>
                An Android sample is available at{' '}
                <Link href="https://github.com/krerk/ChulaSSO.Android" target="_blank" rel="noreferrer">
                    github.com/krerk/ChulaSSO.Android
                </Link>.
                ClassDeeDee (<Link href="https://classdeedee.cp.eng.chula.ac.th/" target="_blank" rel="noreferrer">
                classdeedee.cp.eng.chula.ac.th
                </Link>) also uses both the ticket flow and QR login and can serve as a reference implementation.
            </Paragraph>
        ),
    },
];

export default function Docs() {
    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>

            {/* ── Overview ──────────────────────────────────────── */}
            <Card>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Title level={3} style={{ marginBottom: 4 }}>ChulaSSO Documentation</Title>
                    <Paragraph>
                        <Text strong>ChulaSSO</Text> is the Single Sign-On service for Chulalongkorn
                        University. It lets staff and students authenticate with their Chula IT account
                        across university applications — passwords are entered only on ChulaSSO itself
                        and are never exposed to third-party apps.
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                        Originally developed as part of the Chula DataGateway project (2015) by{' '}
                        <Text strong>Krerk Piromsopa, Ph.D.</Text> The current version (ChulaSSO 2.0)
                        runs on FastAPI + MariaDB at{' '}
                        <Link href="https://account.it.chula.ac.th/">account.it.chula.ac.th</Link>.
                    </Paragraph>
                </Space>
            </Card>

            {/* ── Getting started ───────────────────────────────── */}
            <Card title={<><CodeOutlined style={{ marginRight: 8 }} />Getting started</>}>
                <Paragraph>
                    Register your app via <Link href="/html/myapps">Developer — My Apps</Link> to
                    obtain an <Text code>app_id</Text> and <Text code>app_secret</Text>. Two login
                    flows are available — select a tab to see the full sequence diagram.
                </Paragraph>
                <Tabs items={FLOW_TABS} />
            </Card>

            {/* ── API Reference ─────────────────────────────────── */}
            <Card title={<><ApiOutlined style={{ marginRight: 8 }} />API Reference</>}>
                <Space direction="vertical" size={28} style={{ width: '100%' }}>

                    <div>
                        <Title level={5}>GET /login</Title>
                        <Paragraph type="secondary">
                            Redirect the user's browser here to start authentication.
                        </Paragraph>
                        <Descriptions size="small" bordered column={1}>
                            <Descriptions.Item label="Query params">
                                <Text code>service</Text> — callback URL (ticket appended on success)<br />
                                <Text code>app_id</Text> — your app identifier<br />
                                <Text code>serviceName</Text> — display name shown on the grant screen (optional)
                            </Descriptions.Item>
                            <Descriptions.Item label="Response">
                                302 → <Text code>{'{service}'}?ticket={'{ticket_id}'}</Text>
                            </Descriptions.Item>
                        </Descriptions>
                    </div>

                    <div>
                        <Title level={5}>POST /serviceValidation</Title>
                        <Paragraph type="secondary">
                            Redeem a ticket from your backend server. Never call this from the browser.
                        </Paragraph>
                        <Descriptions size="small" bordered column={1}>
                            <Descriptions.Item label="Headers">
                                <Text code>DeeAppId</Text> &nbsp;
                                <Text code>DeeAppSecret</Text> &nbsp;
                                <Text code>DeeTicket</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="200">JSON user profile (see table below)</Descriptions.Item>
                            <Descriptions.Item label="401">
                                <Text code>{'{"info": "No permission."}'}</Text>
                            </Descriptions.Item>
                        </Descriptions>
                    </div>

                    <div>
                        <Title level={5}>
                            POST /qrValidation&nbsp;<Tag color="green">new</Tag>
                        </Title>
                        <Paragraph type="secondary">
                            Redeem a ChulaSSO QR token. Tokens have a 20-second TTL and are
                            one-time use. Pass the <em>raw scanned URL</em> as{' '}
                            <Text code>DeeQrToken</Text>.
                        </Paragraph>
                        <Descriptions size="small" bordered column={1}>
                            <Descriptions.Item label="Headers">
                                <Text code>DeeAppId</Text> &nbsp;
                                <Text code>DeeAppSecret</Text> &nbsp;
                                <Text code>DeeQrToken</Text> — full scanned URL
                            </Descriptions.Item>
                            <Descriptions.Item label="200">JSON user profile (same as serviceValidation)</Descriptions.Item>
                            <Descriptions.Item label="401">
                                <Text code>{'{"info": "..."}'}</Text> — expired / already used / bad credentials
                            </Descriptions.Item>
                        </Descriptions>
                    </div>

                    <div>
                        <Title level={5}>User profile payload</Title>
                        <Paragraph type="secondary">
                            Returned by both <Text code>/serviceValidation</Text> and{' '}
                            <Text code>/qrValidation</Text> on success (HTTP 200).
                        </Paragraph>
                        <Table
                            size="small"
                            pagination={false}
                            dataSource={PROFILE_FIELDS}
                            columns={PROFILE_COLUMNS}
                            rowKey="field"
                        />
                    </div>

                </Space>
            </Card>

            {/* ── FAQ ───────────────────────────────────────────── */}
            <Card title={<><QuestionCircleOutlined style={{ marginRight: 8 }} />FAQ</>}>
                <Collapse items={FAQ} bordered={false} />
            </Card>

        </Space>
    );
}
